import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../Contexts/APIContext";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { ScorecardSectionViewModel } from "../ViewModels/ScorecardSectionViewModel";
import Tooltip from "@mui/material/Tooltip";
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { GetMonitoringScorecardResponseViewModel } from "../ViewModels/GetMonitoringScorecardResponse";
import MonitoringSection from "./MonitoringSection";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from "@mui/material/AccordionDetails";
import CircularProgress from "@mui/material/CircularProgress";
import Stepper from "@mui/material/Stepper";
import StepButton from "@mui/material/StepButton";
import Step from "@mui/material/Step";
import StepIcon from "@mui/material/StepIcon";
import DoneIcon from '@mui/icons-material/Done';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FlagIcon from '@mui/icons-material/Flag';
import EditScorecardButton from "../EditScorecardButton";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Badge from '@mui/material/Badge';
import FlaggedIssuesModal from "../Flagged Issues/FlaggedIssuesModal";
import { ScorecardContext } from "../../Contexts/ScorecardContext";

const styles = {
    fullHeight: {
        height: '100%'
    },
    scroll: {
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    heading: {
        backgroundColor: "#8080801f"
    }
};
interface IProps {
    callRefId: string;
    scorecardId: number;
    backToMenu?: (firstLoad: boolean) => void;
    scorecardBuilderView: boolean;
    feedbackView?: string;
    rescoreView?: boolean;
    checkTLQuestionsAnswered?: () => void;
    pdf?: string;
    scorecardCount?: number;
    setTime?: (time: string) => void;
    mainView?: boolean;
    toggleTimerActive?: (active: boolean) => void;
}

export default function ScorecardTemplate(props: IProps) {
    const { get, showMessage, isQA, isTeamLeader, isAdmin, isAgent, isExec, isClient } = useContext(APIContext);
    const { areAllQuestionsSaved, areAnyQuestionsSaving } = useContext(ScorecardContext);
    const [monitoringScorecard, setMonitoringScorecard] = useState<GetMonitoringScorecardResponseViewModel | null>(null);
    const [expandedSection, setExpandedSection] = useState<number>(-1);
    const [sectionHeadings, setSectionHeadings] = useState<ScorecardSectionViewModel[]>([]);
    const [answerCount, setAnswerCount] = useState<number>(0);
    const [questionCount, setQuestionCount] = useState<number>(0);
    const [score, setScore] = useState<number>(0);
    const [penalty, setPenalty] = useState<number>(0);
    const [maxScore, setMaxScore] = useState<number>(0);
    const [monitor, setMonitor] = useState<string>("");
    const [_, setClient] = useState<string>("");
    const [_2, setAgentName] = useState<string>("");
    const [pages, setPages] = useState<number[]>([]);
    const [deleteScorecardDialog, setDeleteScorecardDialog] = useState<boolean>(false);
    // Conditionally renders scorecard timer, timer is hidden on scorecard completion, 
    // timer still increments while hidden may need fix in future to allow stoping and starting
    const [loading, setLoading] = useState<boolean>(false);
    const [flaggedIssues, setFlaggedIssues] = useState(0);
    const [openFlaggedIssuesModal, setOpenFlaggedIssuesModal] = useState(false);

    useEffect(() => {
        initialise();
    }, []);

    useEffect(() => {
        if (monitoringScorecard) {
            getMonitor();
            update();
        }
    }, [monitoringScorecard])

    useEffect(() => {
        getDownloadData();
    }, [props.callRefId]);

    useEffect(() => {
        if (props.callRefId !== "" && monitoringScorecard !== null && monitoringScorecard.templateId !== 0)
            getFlaggedIssues();
    }, [props.callRefId, monitoringScorecard]);

    const getFlaggedIssues = () => {
        get('MonitoringScorecard', `GetFlaggedIssuesCount?templateId=${monitoringScorecard!.templateId}&callRefId=${props.callRefId}`, (data) => {
            setFlaggedIssues(data);
        });
    }

    const getDownloadData = () => {
        get('PDFGenerator', `GetClient?callRefId=${props.callRefId}`, (data) => {
            setClient(data)
        });
        get('PDFGenerator', `GetAgentName?callRefId=${props.callRefId}`, (data) => {
            setAgentName(data)
        });
    }

    const initialise = () => {
        setLoading(true);
        let controllerAction = `LoadScorecard?completeId=${props.scorecardId}`;
        if (props.feedbackView === "Team Leader Feedback View") {
            controllerAction += `&view=${props.feedbackView}`
        }
        get('MonitoringScorecard', controllerAction, (data) => {
            setMonitoringScorecard(data);
            setPagesArray(data.maxPage);
            if (props.toggleTimerActive) {
                props.toggleTimerActive(!data.complete);
            }
        })
    }

    const getPageStepper = (page: number) => () => {
        get('MonitoringScorecard', `GetMonitoringScorecard?callRefId=${props.callRefId}&completeId=${monitoringScorecard!.existingId}&page=${page}`, (data) => {
            setMonitoringScorecard(data)
            setPagesArray(data.maxPage)
            update(page);
        });
    }

    const setPagesArray = (pages: number) => {
        let pageArray: number[] = [];
        for (var i = 0; i < pages; i++) {
            pageArray[i] = i;
        }
        setPages(pageArray);
    }

    const getMonitor = () => {
        get('MonitoringScorecard', `GetMonitor?completeId=${monitoringScorecard!.existingId}`,
            (data) => {
                setMonitor(data);
            })
    }

    const handleSectionOpen = (panel: number) => () => {
        setExpandedSection(expandedSection !== panel ? panel : -1);
    };

    const handleNext = () => {
        if (expandedSection != sectionHeadings.length - 1) {
            setExpandedSection(expandedSection + 1);
            return;
        }

        if (areAnyQuestionsSaving()) {
            showMessage('error', 'Unsaved questions')
            return;
        }

        if (!checkPageComplete()) {
            showMessage('error', 'Page is not complete');
            return;
        }

        if (!areAllQuestionsSaved()) {
            showMessage('error', 'Unsaved questions')
            return;
        }

        get('MonitoringScorecard', `ContinueScorecard?callId=${props.callRefId}&completeId=${monitoringScorecard!.existingId}&page=${monitoringScorecard!.page}`, (data) => {
            if (monitoringScorecard?.page == data.maxPage) {
                showMessage('success', 'Scorecard Submitted');
                // Timer is not re-enabled after a scorecard is completed, this may need revising in future
                if (data.complete) {
                    if (props.toggleTimerActive) {
                        props.toggleTimerActive(!data.complete);
                    }
                }
            }
            setExpandedSection(-1);
            setMonitoringScorecard(data);
            update(data.page);
        });
    };

    const update = (page?: number) => {
        let controllerAction = `UpdateScorecard?completeId=${monitoringScorecard!.existingId}&page=${page ?? monitoringScorecard!.page ?? 1}`;
        if (props.feedbackView === "Team Leader Feedback View") {
            controllerAction += `&view=${props.feedbackView}`
        }
        get('MonitoringScorecard', controllerAction, (data) => {
            setAnswerCount(data.answerCount);
            setQuestionCount(data.questionCount);
            setScore(data.score);
            setMaxScore(data.maxScore);
            setSectionHeadings(data.sections);
            setPenalty(data.penalty)
            props.checkTLQuestionsAnswered?.();
            setLoading(false);
        })
    }

    const editCompletedScorecard = () => {
        setMonitoringScorecard({ ...monitoringScorecard!, complete: false })
    }

    const checkPageComplete = () => {
        let complete = true;
        sectionHeadings.forEach(s => {
            if (!s.validated)
                complete = false;
        })
        return complete;
    }

    const handleDeleteScorecard = () => {
        get('MonitoringScorecard', `RemoveScorecardComplete?completeId=${props.scorecardId}`, () => {
            if (!props.scorecardBuilderView && props.backToMenu) {
                backToMenu();
            }
            setDeleteScorecardDialog(false);
        });
    }

    const toggleFlagsModal = (refresh: boolean) => {
        if (refresh) getFlaggedIssues();
        setOpenFlaggedIssuesModal((prev) => !prev);
    }

    const getScorecard = () => {
        get('MonitoringScorecard', `LoadScorecard?completeId=${props.scorecardId}`, (data) => {
            setMonitoringScorecard(data);
            setPagesArray(data.maxPage);
        });
    }

    const deleteAllAnswers = () => {
        get('ScorecardManagement', `RemoveScorecardAnswers?completeId=${props.scorecardId}`, () => {
            getScorecard();
        });
    }

    const backToMenu = () => {
        setMonitoringScorecard(null);
        if (props.backToMenu) {
            props.backToMenu(false);
        }
    }

    const calculateScore = () => {
        let criticalFailure = sectionHeadings.filter(f => f.criticalFailure).length > 0
        if (criticalFailure) return 0;
        if (score === 0 || maxScore === 0) return 0;
        return Number(((score / maxScore) * 100) - penalty).toFixed(2);
    }

    const canSubmit = (index: number, sectionsCount: number) => {
        return index === sectionsCount - 1 && (!areAnyQuestionsSaving() && areAllQuestionsSaved())
    }

    return (
        <>
            <FlaggedIssuesModal open={openFlaggedIssuesModal} onClose={toggleFlagsModal} scorecard={monitoringScorecard} callRefId={props.callRefId} />
            {
                !props.scorecardBuilderView &&
                <Dialog
                    open={deleteScorecardDialog}
                    onClose={() => setDeleteScorecardDialog(false)}>
                    <DialogTitle id="delete-scorecard-title">
                        {"Delete Scorecard?"}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="delete-scorecard-description">
                            Are you sure you want to delete this scorecard? All scorecard data will be lost
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteScorecardDialog(false)}>Cancel</Button>
                        <Button onClick={handleDeleteScorecard} autoFocus>
                            Delete Scorecard
                        </Button>
                    </DialogActions>
                </Dialog>
            }
            {monitoringScorecard !== null && !loading &&
                <Box display="flex" sx={styles.fullHeight} flexDirection="column">
                    {!props.feedbackView &&
                        <AppBar position="static" color="default" >
                            <Toolbar variant="dense">
                                <Grid container spacing={1} justifyContent="space-between">
                                    {
                                        !props.scorecardBuilderView && !props.rescoreView &&
                                        <Grid>
                                            <Badge
                                                badgeContent={props.scorecardCount ?? 0}
                                                invisible={props.scorecardCount ? props.scorecardCount < 2 : true}
                                                color="primary">
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => backToMenu()}>
                                                    <ArrowBack />
                                                </Button>
                                            </Badge>
                                        </Grid>
                                    }
                                    {
                                        props.scorecardBuilderView &&
                                        <>
                                            <Grid container spacing={1} size={12}>
                                                <Grid>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={getScorecard}>
                                                        Refresh
                                                    </Button>
                                                </Grid>
                                                <Grid>
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        onClick={deleteAllAnswers}>
                                                        Delete all answers
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </>
                                    }
                                    {sectionHeadings.filter(f => f.criticalFailure).length > 0 &&
                                        <Grid>
                                            <Tooltip
                                                title="Critical Failure"
                                                placement="bottom">
                                                <ErrorIcon
                                                    color="error"
                                                    fontSize="large" />
                                            </Tooltip>
                                        </Grid>
                                    }
                                    {monitoringScorecard.complete && (isQA || isExec || isAdmin || isTeamLeader) &&
                                        <>
                                            {(props.pdf === "0" || props.pdf === undefined) &&
                                                <EditScorecardButton
                                                    completeId={props.scorecardId}
                                                    editCompletedScorecard={editCompletedScorecard} />
                                            }
                                            {monitor === "Q-AI" && (!isAgent || isAdmin) &&
                                                <Grid>
                                                    <Button
                                                        onClick={() => toggleFlagsModal(false)}
                                                    >
                                                        <Tooltip
                                                            title="Flag Issue"
                                                            placement="bottom"
                                                        >
                                                            {flaggedIssues !== 0 ?
                                                                <Badge max={999} badgeContent={flaggedIssues} color="error">
                                                                    <FlagIcon
                                                                        color="primary"
                                                                        fontSize="medium"
                                                                    />
                                                                </Badge>
                                                                :
                                                                <FlagIcon
                                                                    color="primary"
                                                                    fontSize="medium"
                                                                />
                                                            }
                                                        </Tooltip>
                                                    </Button>
                                                </Grid>
                                            }
                                        </>
                                    }
                                    <Grid>
                                        <Typography variant="h6">
                                            {monitor}
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="h6">
                                            {monitoringScorecard.name}
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography
                                            variant="h6"
                                            textAlign="right">
                                            Progress: {answerCount}/{questionCount}
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="h6" textAlign="right">
                                            Score: {calculateScore()}%

                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Toolbar>
                        </AppBar>
                    }
                    {props.pdf && props.pdf !== "0" &&
                        <Box sx={{ overflowY: 'hidden', height: '100%' }}>
                            <Grid size={12} sx={{ overflow: 'hidden', height: '100%', width:'100%' }}>
                                <object width="100%" height="100%" data={props.pdf + "#toolbar=0&view=FitH"} type="application/pdf" />
                            </Grid>
                        </Box >
                    }
                    <Box sx={props.pdf !== '0' ? { overflowY: 'hidden' } : styles.scroll}>
                        <Grid container>
                            {!monitoringScorecard.activeTemplate && !props.scorecardBuilderView && !props.feedbackView &&
                                <Grid size={12} textAlign="center" marginTop="8px">
                                    <Typography variant="h6" color="#d32f2f">This scorecard is out of date click <span onClick={() => setDeleteScorecardDialog(true)}><u> here</u></span> to delete</Typography>
                                </Grid>
                            }
                            {(props.pdf === undefined || props.pdf === "0") && !isClient &&
                                <>
                                    <Grid size={1} />
                                    <Grid size={10} padding={2}>
                                        <Stepper nonLinear activeStep={monitoringScorecard.page - 1}>
                                            {pages.map((label, index) => (
                                                <Step key={label} >
                                                    <StepButton color="inherit" onClick={getPageStepper(index + 1)} disabled={!monitoringScorecard.complete && (index + 1 > monitoringScorecard.page)} />
                                                </Step>
                                            ))}
                                            <Step completed={monitoringScorecard.complete} last disabled>
                                                {monitoringScorecard.complete ?
                                                    <StepButton color="inherit" />
                                                    :
                                                    <StepIcon icon={<DoneIcon color="disabled" />} />
                                                }
                                            </Step>
                                        </Stepper>
                                    </Grid>
                                    <Grid size={1} />
                                    {monitoringScorecard.sections.map((section, index) =>
                                        <Grid size={12} key={index} >
                                            <Accordion expanded={expandedSection === index} onChange={handleSectionOpen(index)} disableGutters square variant="outlined" sx={{ padding: '0px' }}>
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon sx={{ marginLeft: '8px' }} />} sx={styles.heading}
                                                >
                                                    {sectionHeadings.length == monitoringScorecard.sections.length &&
                                                        <Grid container spacing={1} alignItems="center">
                                                            <Grid height={32}>
                                                                {(sectionHeadings[index].validated) ?
                                                                    <Tooltip title="Complete" placement="top"><CheckCircleIcon color="primary" /></Tooltip>
                                                                    :
                                                                    <CircularProgress variant="determinate" value={
                                                                        (sectionHeadings[index].answerCount
                                                                            / sectionHeadings[index].questionCount * 100)
                                                                    } size={22} thickness={8} color="primary" />
                                                                }
                                                            </Grid>
                                                            <Grid>
                                                                <Typography variant="subtitle1" ><b>{section.name}</b></Typography>
                                                            </Grid>
                                                            {sectionHeadings.filter(f => f.id == section.id && f.criticalFailure).length > 0 &&
                                                                <Grid height={32}>
                                                                    <Tooltip title="Critical Failure" placement="top"><ErrorIcon color="error" /></Tooltip>
                                                                </Grid>
                                                            }
                                                            <Grid flexGrow={1} />
                                                            {sectionHeadings[index].validated &&
                                                                <Grid>
                                                                    {
                                                                        (!isNaN(sectionHeadings[index].score
                                                                            / sectionHeadings[index].maxScore * 100)) &&
                                                                        <Typography variant="subtitle1">Score: {(sectionHeadings[index].score
                                                                            / sectionHeadings[index].maxScore * 100).toFixed(2)}%</Typography>
                                                                    }
                                                                </Grid>
                                                            }
                                                        </Grid>
                                                    }
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <MonitoringSection
                                                        section={section}
                                                        readOnly={monitoringScorecard.complete}
                                                        completeId={monitoringScorecard.existingId!}
                                                        callRefId={props.callRefId}
                                                        updateParent={update}
                                                        answers={monitoringScorecard.answers}
                                                        feedbackView={props.feedbackView}
                                                    />
                                                    {
                                                        !props.feedbackView && !monitoringScorecard.complete &&
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={index === monitoringScorecard.sections.length - 1 && !canSubmit(index, monitoringScorecard.sections.length)}
                                                            fullWidth
                                                            onClick={handleNext}>
                                                            {index === monitoringScorecard.sections.length - 1
                                                                ? !areAnyQuestionsSaving()
                                                                    ? "Submit"
                                                                    : < CircularProgress size={25} sx={{ color: 'white' }} />
                                                                : "Continue"
                                                            }
                                                        </Button>
                                                    }
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>
                                    )}
                                </>
                            }
                        </Grid>
                    </Box>
                </Box >
            }
        </>
    );
}